import { FILLS, STROKES } from '../styles/tokens';

const PLACEHOLDER_URL_PREFIX = 'data:image/svg+xml;utf8,';

/**
 * Returns a data-URL SVG placeholder used in image-empty states.
 *
 * The SVG is encoded inline so the browser renders it without a network
 * hop — replacing the previous external-CDN fallback that leaked a
 * silent dependency on a third-party image host.
 *
 * Token-tinted via `FILLS.surface` (background) and `STROKES.subtle`
 * (1px stroke) so the placeholder matches the dark-luxury empty-state
 * affordance used elsewhere in the app. Optional `label` is rendered
 * centred for debug clarity (helps identify which call site produced
 * a given placeholder in DevTools); defaults to no label.
 *
 * Pure: same input always yields the same output, no randomness, no
 * time-dependent values. This keeps the helper SSR-safe and snapshot-
 * stable.
 */
export function placeholderImageUrl(label?: string): string {
  // 800x400 — matches the largest dimension previously requested from
  // the external CDN fallback, which lets every consumer drop the helper
  // in without a separate size-negotiation step.
  const backgroundFill = FILLS.surface;
  const borderStroke = STROKES.subtle;

  const textElement =
    label !== undefined && label.length > 0
      ? `<text x="400" y="200" text-anchor="middle" dominant-baseline="middle" font-family="sans-serif" font-size="24" fill="${borderStroke}">${escapeXmlText(label)}</text>`
      : '';

  const svgBody =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400" width="800" height="400">` +
    `<rect width="800" height="400" fill="${backgroundFill}" stroke="${borderStroke}" stroke-width="1"/>` +
    textElement +
    `</svg>`;

  return `${PLACEHOLDER_URL_PREFIX}${encodeURIComponent(svgBody)}`;
}

/**
 * Escapes XML-significant characters in user-supplied label text so they
 * survive embedding in the SVG `<text>` body. Only the five XML-required
 * entities — caller-controlled label could otherwise break the SVG parse.
 */
function escapeXmlText(rawText: string): string {
  return rawText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** True for an empty URL or one produced by `placeholderImageUrl`: no real photo behind it. */
export function isPlaceholderImageUrl(imageUrl: string): boolean {
  return imageUrl === '' || imageUrl.startsWith(PLACEHOLDER_URL_PREFIX);
}
